import React, { useState } from 'react';
import FileUpload from './components/FileUpload';
import Results from './components/Results';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const APP_PASSWORD = process.env.REACT_APP_PASSWORD || '';

function PasswordGate({ onUnlock }) {
  const [input, setInput] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input === APP_PASSWORD) {
      onUnlock();
    } else {
      setError(true);
      setInput('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-lg p-10 w-full max-w-sm">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-1">AOL Syllabus Analyzer</h1>
          <p className="text-gray-500 text-sm">Enter the password to continue</p>
        </div>
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            value={input}
            onChange={(e) => { setInput(e.target.value); setError(false); }}
            placeholder="Password"
            className="w-full border border-gray-300 rounded-lg px-4 py-3 mb-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            autoFocus
          />
          {error && (
            <p className="text-red-500 text-sm mb-3 text-center">Incorrect password. Please try again.</p>
          )}
          <button
            type="submit"
            className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
          >
            Enter
          </button>
        </form>
        <p className="text-center text-gray-400 text-xs mt-6">Illinois State University · AEI Fellowship</p>
      </div>
    </div>
  );
}

function App() {
  const [unlocked, setUnlocked] = useState(APP_PASSWORD === '');
  const [analysisResults, setAnalysisResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processingStatus, setProcessingStatus] = useState(null);

  if (!unlocked) {
    return <PasswordGate onUnlock={() => setUnlocked(true)} />;
  }

  const handleFileUpload = async (files, analysisMode) => {
    setLoading(true);
    setError(null);
    setAnalysisResults([]);
    setProcessingStatus({ total: files.length, current: 0, currentFile: '' });

    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    formData.append('analysisMode', analysisMode);

    try {
      const response = await axios.post(`${API_BASE_URL}/analyze`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const data = response.data;
      
      if (data.success && data.results && data.results.length > 0) {
        setAnalysisResults(data.results);
        
        // Show errors if any files failed
        if (data.errors && data.errors.length > 0) {
          const errorMessages = data.errors.map(e => `${e.filename}: ${e.error}`).join('\n');
          setError(`Some files failed to process:\n${errorMessages}`);
        }
      } else if (data.errors && data.errors.length > 0) {
        const errorMessages = data.errors.map(e => `${e.filename}: ${e.error}`).join('\n');
        setError(`All files failed to process:\n${errorMessages}`);
      } else {
        setError('No results returned from analysis');
      }
      
      setLoading(false);
      setProcessingStatus(null);
    } catch (err) {
      console.error('Error uploading files:', err);
      const errorMessage = err.response?.data?.error || 'Failed to analyze syllabi. Please try again.';
      setError(errorMessage);
      setLoading(false);
      setProcessingStatus(null);
    }
  };

  const handleReset = () => {
    setAnalysisResults([]);
    setError(null);
    setProcessingStatus(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        <header className="flex items-center justify-between mb-8 bg-white p-4 rounded-lg shadow-sm">
          <div className="flex-shrink-0 w-1/4">
            <img
              src="/isu-logo.png"
              alt="Illinois State University"
              className="h-16 w-auto object-contain"
            />
          </div>
          
          <div className="text-center w-1/2">
            <h1 className="text-3xl font-bold text-gray-800 mb-1">
              AOL Multiple Syllabus Analyzer
            </h1>
            <p className="text-gray-600 text-sm">
              Analyze multiple course syllabi against Assurance of Learning goals
            </p>
          </div>

          <div className="flex-shrink-0 w-1/4 flex items-center justify-end">
            <img
              src="/sandeep-jagani-aei.png"
              alt="Adaptive Edge Institute"
              className="h-16 w-auto object-contain"
            />
          </div>
        </header>

        <main>
          {analysisResults.length === 0 && !loading && (
            <FileUpload onFileUpload={handleFileUpload} />
          )}

          {loading && (
            <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8">
              <div className="flex flex-col items-center justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mb-4"></div>
                <h2 className="text-xl font-semibold text-gray-700 mb-2">
                  Analyzing syllabi...
                </h2>
                {processingStatus && (
                  <div className="text-center">
                    <p className="text-gray-500 mb-2">
                      Processing {processingStatus.total} file{processingStatus.total !== 1 ? 's' : ''}
                    </p>
                    <p className="text-sm text-gray-400">
                      This may take several minutes depending on the number of files.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="max-w-2xl mx-auto bg-red-50 border border-red-200 rounded-lg shadow-lg p-6 mb-6">
              <div className="flex items-start">
                <svg
                  className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-red-800 mb-1">
                    Error
                  </h3>
                  <p className="text-red-700 whitespace-pre-line">{error}</p>
                </div>
              </div>
              {analysisResults.length === 0 && (
                <button
                  onClick={handleReset}
                  className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Try Again
                </button>
              )}
            </div>
          )}

          {analysisResults.length > 0 && (
            <Results
              results={analysisResults}
              onReset={handleReset}
            />
          )}
        </main>

        <footer className="text-center mt-12 text-gray-500 text-sm">
          <p>Developed by Dr. Sandeep Jagani in association with Adaptive Edge Institute</p>
        </footer>
      </div>
    </div>
  );
}

export default App;
